<template>
  <!--SECTION INDUSTRIES-->
  <main>
    <div class="bank">
      <div class="container-db bread-container">
        <div class="bread-crumbs">
          <router-link to="/">Home</router-link>
          <span class="bread-separator">/</span>
          <span class="bread-current">DoctorMed</span>
        </div>
      </div>
      <div class="container-db">
        <h1 class="bank-title">DoctorMed – An Innovative Platform for Online Medical Consultations</h1>
      </div>
    </div>
    <div class="descrip-result-wrapper">
      <div class="descrip-result-text">
        <div class="container-db">
          <div class="decription-container">
            <div class="description portfolio-container">
              <div class="portfolio-container-top">
                <div>
                  <h2>How Does DoctorMed Differ from Other Platforms Like ZocDoc?</h2>
                  <p>
                    DoctorMed is designed as a more flexible and technologically advanced alternative to online doctor appointment platforms like ZocDoc. The key differences include:
                  </p>

                  <div class="tech-features-section">
                    <div class="container-db bank-tech">
                      <div class="tech-features-wrapper">
                        <div class="features">
                          <ul>
                            <li class="marked-item">Deeper Telemedicine Integration – The system supports video chats, synchronization with medical records, and patient health analytics.</li>
                            <li class="marked-item">Artificial Intelligence (AI) Algorithms – Automatic doctor selection based on symptoms, medical history, and specialist ratings.</li>
                          </ul>
                        </div>
                        <ul class="features">
                          <li class="marked-item">Advanced Features for Doctors – Personalized dashboards, appointment analytics, and integration with medical systems</li>
                          <li class="marked-item">
                            Flexible Payment System – Subscription plans for doctors and patients, commission-based payments for consultations, and the possibility of free consultations with government support.
                          </li>
                        </ul>
                      </div>
                      <div class="tech-featuchers-wrapper">
                        <div class="technologies-wrapper">
                          <h2 class="tech-features-title">Technical Architecture & Technology Choices</h2>
                          <p>DoctorMed utilizes AWS, <router-link to="/services/laravel-development-company">Laravel</router-link>, and VueJS, chosen for the following reasons:</p>
                          <ul class="features">
                            <li class="marked-item">
                              AWS – Flexible scalability, reliability, AI/ML service integration capabilities, and a high level of security.
                            </li>
                            <li class="marked-item">
                              Laravel – Fast development speed, security, seamless integration with databases and cloud services.
                            </li>
                            <li class="marked-item">
                              VueJS – Lightweight and fast UI performance, which is critical for high platform efficiency.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <p>Scalability is achieved through a microservices architecture, enabling the easy addition of new features and adaptation to various markets.</p>
                    </div>
                  </div>
                </div>
                <img
                  class="doctor-img"
                  v-lazyload
                  data-url="/img/images-db/case-studies/DoctorMed.webp"
                  alt="Healthcare software development"
                  title="Healthcare software development"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container-db bank-tech additional-tech-description">
        <h2>Video Communication Functionality</h2>
        <p>The video chat system is built on WebRTC, providing:</p>
        <ul class="features">
          <li class="marked-item">
            Reliable and secure peer-to-peer communication.
          </li>
          <li class="marked-item">
            Automatic quality adjustment based on internet speed.
          </li>
          <li class="marked-item">
            Built-in screen sharing and document collaboration, allowing doctors to work with medical records in real
            time.
          </li>
        </ul>
    </div>
    <div class="container-db bank-tech additional-tech-description">
        <h2>Cloud Service Integrations</h2>
        <p>DoctorMed supports Dropbox, PCloud, and Google Drive, allowing both patients and doctors to conveniently store and exchange medical documents.</p>
        <ul class="features">
          <li class="marked-item">
            All files are automatically encrypted before transmission.
          </li>
          <li class="marked-item">
            Automatic synchronization ensures real-time updates to patient histories.
          </li>
        </ul>
    </div>
    <div class="container-db bank-tech additional-tech-description">
        <h2>Mobile Version & Applications</h2>
        <p>DoctorMed offers iOS and Android mobile applications, developed using Flutter, allowing for quick adaptation across various devices.</p>
    </div>
    <div class="container-db bank-tech additional-tech-description">
        <h2>Ratings & Reviews System</h2>
        <ul class="features">
          <li class="marked-item">
            Doctors are rated based on diagnosis accuracy, consultation quality, wait times, and communication skills.
          </li>
          <li class="marked-item">
            AI moderation of reviews is implemented to prevent fake or manipulated feedback.
          </li>
        </ul>
    </div>
    <div class="container-db bank-tech additional-tech-description">
      <h2>Appointment Reminders & Health Calendar</h2>
      <ul class="features">
        <li class="marked-item">
          Integration with Google Calendar, Apple Health, and Microsoft Outlook.
        </li>
        <li class="marked-item">
          Automated SMS and push notifications to remind patients of upcoming appointments.
        </li>
      </ul>
    </div>
    <div class="container-db bank-tech additional-tech-description">
      <h2>Telemedicine & Medical Records</h2>
      <ul class="features">
        <li class="marked-item">
          Available for all specialties – from general practitioners to highly specialized doctors.
        </li>
        <li class="marked-item">
          Patients can link their medical records through integration with Electronic Health Records (EHR) systems.
        </li>
      </ul>
    </div>
    <div class="container-db bank-tech additional-tech-description last-tech-description">
      <h2>Security & GDPR Compliance</h2>
      <p>DoctorMed employs two-factor authentication, end-to-end data encryption, and HIPAA certification to ensure information security.</p>
      <ul class="features">
        <li class="marked-item">
          GDPR compliance – Users can request the deletion of their personal data.
        </li>
        <li class="marked-item">
          AWS Shield and AWS WAF – Protection against DDoS attacks and unauthorized access.
        </li>
      </ul>
    </div>
    <AcquaintedSection />
    <div
      v-lazyload
      data-src="/img/images-db/parallax/stone2.webp"
      class="lazy-bg bottom-stone"
    ></div>
    <script v-html="jsonld" type="application/ld+json"></script>
  </main>
</template>
<script>
import Slick from "vue-slick";
import AcquaintedSection from "../AcquaintedSection";
let vm = {};
export default {
  name: "DoctorMed",
  metaInfo: {
    title: "DoctorMed",
    meta: [
      {
        vmid: "description",
        name: "description",
        content:
          "Discover how DoctorMed revolutionizes telemedicine with AI-driven doctor selection, seamless video consultations, and secure medical record integration.",
      },
      {
        vmid: "og:image:type",
        name: "og:image:type",
        content: "image/webp",
      },
      {
        vmid: "og:title",
        name: "og:title",
        content:
          "DoctorMed – Scalable Telemedicine Platform | DevBrother",
      },
      {
        vmid: "og:description",
        name: "og:description",
        content:
          "Discover how DoctorMed revolutionizes telemedicine with AI-driven doctor selection, seamless video consultations, and secure medical record integration.",
      },
      // {
      //   vmid: "og:image",
      //   name: "og:image",
      //   content:
      //     "https://devbrother.com/img/images-db/case-studies/art-solutions.webp",
      // },
      {
        vmid: "og:type",
        name: "og:type",
        content: "website",
      },
      // {
      //   vmid: "og:url",
      //   name: "og:url",
      //   content: "https://devbrother.com/cases/art-galleries",
      // },
      {
        vmid: "twitter:title",
        name: "twitter:title",
        content:
          "DoctorMed – Scalable Telemedicine Platform | DevBrother",
      },
      {
        vmid: "twitter:description",
        name: "twitter:description",
        content:
          "Discover how DoctorMed revolutionizes telemedicine with AI-driven doctor selection, seamless video consultations, and secure medical record integration.",
      },
      // {
      //   vmid: "twitter:image",
      //   name: "twitter:image",
      //   content:
      //     "https://devbrother.com/img/images-db/case-studies/art-solutions.webp",
      // },
    ],
  },
  components: { Slick, AcquaintedSection },
  mounted() {
			this.addCanonicalLink();
  },
	methods: {
			addCanonicalLink: () => {
				const link = document.createElement('link');
				link.setAttribute('rel', 'canonical');
				link.setAttribute('href', 'https://devbrother.com/cases/doctormed');

				const oldLink = document.querySelector('link[rel="canonical"]');
				if (oldLink) {
					document.head.removeChild(oldLink);
				}

				document.head.appendChild(link);
			},
	},
  data() {
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "IndividualProduct",
      name: "Doctormed",
      description: "Discover how DoctorMed revolutionizes telemedicine with AI-driven doctor selection, seamless video consultations, and secure medical record integration.",
    };
    return {
      jsonld,
      slickOptions: {
        prevArrow:
          '<div class="prev-arrow"><img src="/img/images-db/Arrow.svg" alt="arrow" class="arrow" width="26" height="11"/><img src="/img/images-db/Arrow-hover.svg" alt="arrow" class="arrow-hover" width="26" height="11"/></div>',
        nextArrow:
          '<div class="next-arrow"><img src="/img/images-db/Arrow.svg" alt="arrow" class="arrow" width="26" height="11"/><img src="/img/images-db/Arrow-hover.svg" alt="arrow" class="arrow-hover" width="26" height="11"/></div>',
        slidesToShow: 4,
        slidesToScroll: 1,
        // centerMode: true,
        responsive: [
          {
            breakpoint: 769,
            settings: {
              slidesToShow: 1,
              centerMode: false,
              prevArrow: "",
              nextArrow: "",
              dots: true,
              customPaging: function (slider, i) {
                return '<div class="slick-mobile-dot"></div>';
              },
            },
          },
        ],
        // Any other options that can be got from plugin documentation
      },
    };
  },
};
</script>
<style scoped>
.features {
  margin-top: 20px;
}

.additional-tech-description {
  margin-top: 100px;
}

.last-tech-description {
  margin-bottom: 250px;
}
</style>
